import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro, post } from '../api';
import type { Categoria } from './dados';

type Valor = boolean | null;
interface TipoPadrao {
  chave: string;
  categoria: Categoria;
  app: Valor;
  push: Valor;
  /** Valor quando a linha é nula: a fábrica (padrão do papel) ou o padrão do papel (exceção do membro). */
  fabrica?: { app: boolean; push: boolean };
  padrao?: { app: boolean; push: boolean };
}
const CANAIS = ['app', 'push'] as const;

/**
 * Lista de tipos e canais com três valores (nulo, ligado ou desligado) e um Salvar só. Fase 2 (W3) para o padrão do papel;
 * Fase 2 (W22, K8) também para as exceções de um membro em Admin > Usuários (a mesma tabela que a pessoa grava em Minha conta).
 */
function PainelPadroes({ caminho, titulo, texto, rotuloNulo, editavel, marca, voltarTudo }: { caminho: string; titulo: string; texto: string; rotuloNulo: (valor: string) => string; editavel: boolean; marca: Record<string, string>; /** Lote de 21/10 (item 2): rota que apaga todas as exceções. */ voltarTudo?: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const chave = ['notificacoes', caminho];
  const q = useQuery({ queryKey: chave, queryFn: () => get<{ tipos: TipoPadrao[] }>(caminho) });
  const [mudancas, setMudancas] = useState<Record<string, Valor>>({});
  const salvar = useMutation({
    mutationFn: () => api('PUT', caminho, { itens: Object.entries(mudancas).map(([k, ativo]) => ({ tipo: k.split('|')[0], canal: k.split('|')[1], ativo })) }),
    onSuccess: async () => {
      setMudancas({});
      toast.sucesso(t('comum.salvo'));
      await qc.invalidateQueries({ queryKey: chave });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const zerar = useMutation({
    mutationFn: () => post<{ removidas: number }>(voltarTudo!),
    onSuccess: async (r) => {
      setMudancas({});
      toast.sucesso(t('notificacoes.voltouTudo', { count: r.removidas }));
      await qc.invalidateQueries({ queryKey: ['notificacoes'] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const rotuloTipo = (c: string) => t(`notificacoes.tipos.${c}.titulo`, { defaultValue: c });
  const ligadoOuNao = (v: boolean) => (v ? t('notificacoes.ligado') : t('notificacoes.desligado'));
  return (
    <div {...marca}>
      <h3 className="kicker mb-1">{titulo}</h3>
      <p className="mb-3 text-xs text-tinta-3">{texto}</p>
      <Estado consulta={q}>
        {(d) => (
          <>
            <ul className="flex flex-col divide-y divide-borda">
              {d.tipos.map((tp) => (
                <li key={tp.chave} className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center sm:justify-between">
                  <span className="min-w-0 text-sm text-tinta-1">{rotuloTipo(tp.chave)}</span>
                  <div className="grid grid-cols-2 gap-2 sm:w-96">
                    {CANAIS.map((canal) => {
                      const k = `${tp.chave}|${canal}`;
                      const v = k in mudancas ? mudancas[k]! : tp[canal];
                      const base = (tp.padrao ?? tp.fabrica)?.[canal] ?? canal === 'app';
                      return (
                        <Selecao
                          key={canal}
                          aria-label={`${rotuloTipo(tp.chave)}: ${t(`notificacoes.canais.${canal}`)}`}
                          disabled={!editavel}
                          value={v === null ? '' : v ? 'sim' : 'nao'}
                          onChange={(e) => setMudancas((m) => ({ ...m, [k]: e.target.value === '' ? null : e.target.value === 'sim' }))}
                          opcoes={[
                            { valor: '', rotulo: `${t(`notificacoes.canais.${canal}`)}: ${rotuloNulo(ligadoOuNao(base))}` },
                            { valor: 'sim', rotulo: `${t(`notificacoes.canais.${canal}`)}: ${t('notificacoes.ligado')}` },
                            { valor: 'nao', rotulo: `${t(`notificacoes.canais.${canal}`)}: ${t('notificacoes.desligado')}` },
                          ]}
                        />
                      );
                    })}
                  </div>
                </li>
              ))}
            </ul>
            {editavel && (
              <div className="mt-3 flex flex-wrap justify-end gap-2">
                {voltarTudo && (
                  <Botao variante="fantasma" disabled={!d.tipos.some((tp) => tp.app !== null || tp.push !== null)} carregando={zerar.isPending} onClick={() => zerar.mutate()}>
                    {t('notificacoes.voltarTudo')}
                  </Botao>
                )}
                <Botao variante="secundario" disabled={!Object.keys(mudancas).length} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
                  {t('comum.salvar')}
                </Botao>
              </div>
            )}
          </>
        )}
      </Estado>
    </div>
  );
}

/**
 * Fase 2 (W3): Admin > Papéis, "Notificações padrão do papel". Cada tipo e canal: fábrica (sem linha), ligado ou desligado.
 * Vale também para papel de fábrica (o padrão de aviso não muda permissão). Salva separado da matriz de permissões.
 */
export function PadroesPapel({ papelId, editavel }: { papelId: string; editavel: boolean }) {
  const { t } = useTranslation();
  return (
    <PainelPadroes
      caminho={`/notificacoes/padroes/${papelId}`}
      titulo={t('notificacoes.padroesTitulo')}
      texto={t('notificacoes.padroesTexto')}
      rotuloNulo={(valor) => t('notificacoes.valorFabrica', { valor })}
      editavel={editavel}
      marca={{ 'data-padroes-papel': '' }}
    />
  );
}

/**
 * Fase 2 (W22, K8): Admin > Usuários, "Notificações deste usuário": as exceções do membro por tipo e canal (nulo: vale o
 * padrão do papel dele). Só os avisos que ELE recebe pela permissão aparecem.
 */
export function ExcecoesMembro({ usuarioId, editavel }: { usuarioId: string; editavel: boolean }) {
  const { t } = useTranslation();
  return (
    <PainelPadroes
      caminho={`/notificacoes/excecoes/${usuarioId}`}
      titulo={t('notificacoes.excecoesTitulo')}
      texto={t('notificacoes.excecoesTexto')}
      rotuloNulo={(valor) => t('notificacoes.valorPadraoPapel', { valor })}
      editavel={editavel}
      marca={{ 'data-excecoes-membro': '' }}
      voltarTudo={`/notificacoes/excecoes/${usuarioId}/padrao`}
    />
  );
}

/**
 * Lote de 21/10 (item 2): Pessoas > Departamentos, "Notificações padrão do departamento". Nulo: vale o padrão do papel de
 * cada pessoa (que pode variar); ligado ou desligado passa na frente do papel. Muda só quem também muda papéis.
 */
export function PadroesDepartamento({ departamentoId, editavel }: { departamentoId: string; editavel: boolean }) {
  const { t } = useTranslation();
  return (
    <PainelPadroes
      caminho={`/pessoas/departamentos/${departamentoId}/notificacoes`}
      titulo={t('notificacoes.padroesDepTitulo')}
      texto={t('notificacoes.padroesDepTexto')}
      rotuloNulo={() => t('notificacoes.valorDoPapel')}
      editavel={editavel}
      marca={{ 'data-padroes-departamento': '' }}
    />
  );
}
